import { BitmapText, Container, Graphics, type Texture, type RenderLayer as PixiRenderLayer } from 'pixi.js';
import { OVERHEAD_FONT_FAMILY, OVERHEAD_FONT_SIZE } from './bakedFont';
import { UoHueSprite } from './uoHueBatch';

/**
 * The renderer-side sprite for the dice-and-cup interactive static (dice-and-cup,
 * ADR-0083). It is the dice analogue of {@link PlayerSprite}, but radically
 * simpler: the dice never moves, never changes art, and has no body/equipment
 * stack - just a fixed billboard the player double-clicks to roll, plus the
 * overhead action emote (`*Name rolls d1,d2*`) the server floats over it.
 *
 * It is its own lean class (not a `PlayerSprite`, not a bare door `Sprite`)
 * because it needs exactly two things a door sprite lacks - **double-click**
 * detection and an **overhead speech surface** - while needing none of the
 * heavyweight walk-cycle/idle/name machinery a character carries. The small
 * amount of overhead-fade bookkeeping here mirrors `PlayerSprite.say`/
 * `advanceSpeech` deliberately, kept self-contained in this new file so the
 * concurrent edits to `playerSprite.ts`/`gameRenderer.ts` stay out of its way.
 */

/** How long a floated roll emote lingers before it has fully faded (mirrors PlayerSprite). */
const SPEECH_TTL_MS = 5000;
/** The trailing slice of {@link SPEECH_TTL_MS} over which a line fades to nothing. */
const SPEECH_FADE_MS = 1200;
/** Vertical gap between stacked lines, and from the cup's top to the first line. */
const SPEECH_LINE_GAP = 15;
/** zIndex for the overhead emote, far above the billboard so it never clips behind art. */
const LABEL_Z = 1000;
/**
 * Maximum gap between two taps on the cup for them to count as a double-click -
 * the same generous UO-range window {@link PlayerSprite} uses for the
 * own-character wardrobe shortcut.
 */
const DOUBLE_CLICK_MS = 300;

/** One active overhead emote line: its label and how long it has left before clearing. */
interface SpeechLine {
  label: BitmapText;
  remainingMs: number;
}

/**
 * A pooled dice-cup sprite: a fixed static billboard plus a transient overhead
 * emote surface, double-clickable to roll. The renderer creates one per dice
 * entity, positions it each frame at the entity's tile, ages its emote on the
 * render clock via {@link update}, and floats a roll result over it via
 * {@link say}.
 */
export class DiceSprite extends Container {
  // The billboard's measured pixel height, cached once at construction (item 5 /
  // ADR-0135). The cup's texture is fixed for its lifetime, so reading
  // `Sprite.height` on every relayout only re-derives the same value from an
  // (unchanging) texture; caching it keeps {@link layoutSpeech} off the bounds path.
  private readonly billboardHeight: number;
  private readonly overheadLayer?: PixiRenderLayer;
  private readonly speechLines: SpeechLine[] = [];
  private nameLabel?: BitmapText;
  /** `performance.now()` of the last single tap, for the double-click detector. */
  private lastTapMs = Number.NEGATIVE_INFINITY;

  /**
   * Fired when the cup is **double-clicked** (within {@link DOUBLE_CLICK_MS}).
   * The renderer wires it to send a `RollDice` up the socket; the cup never rolls
   * locally (the server is authoritative).
   */
  onDoubleClick?: () => void;

  /** Whether an overhead line is currently fading on the render clock. */
  get requiresActivePresentation(): boolean {
    return this.speechLines.length > 0;
  }

  /**
   * Whether the current tap should be **suppressed** because it was really a long-press
   * that already opened the in-world context menu (ADR-0169). The renderer wires it to
   * the same long-press latch the character/rat `click` handlers consult, so a hold over
   * the cup opens the dice-wager menu but never feeds the solo-roll double-click detector
   * ({@link registerTap}). Absent (headless tests) or `false` means the tap counts as usual.
   */
  suppressClick?: () => boolean;

  /**
   * `texture` absent falls back to a legible flat box standing on the tile - the
   * MoongateSprite graceful-absence discipline. The dice cup's art is in the
   * committed statics atlas so its callers always have a texture; the chest pool
   * (PRD #362 Slice B), which reuses this sprite, degrades on a host whose
   * committed atlas predates the chest bake.
   */
  constructor(texture: Texture | undefined, overheadLayer?: PixiRenderLayer) {
    super();
    this.overheadLayer = overheadLayer;

    if (texture) {
      // A billboard hung bottom-centre, exactly like a static/door sprite, so the
      // cup sits on its tile's near corner once the container is positioned there.
      const billboard = new UoHueSprite();
      billboard.texture = texture;
      billboard.anchor.set(0.5, 1);
      billboard.eventMode = 'static';
      billboard.cursor = 'inherit';
      this.addChild(billboard);
      this.billboardHeight = billboard.height;
    } else {
      const FALLBACK_W = 30;
      const FALLBACK_H = 22;
      const box = new Graphics()
        .rect(-FALLBACK_W / 2, -FALLBACK_H, FALLBACK_W, FALLBACK_H)
        .fill({ color: 0x8a5a2b, alpha: 0.95 }); // chest-brown placeholder
      box.eventMode = 'static';
      box.cursor = 'inherit';
      this.addChild(box);
      this.billboardHeight = FALLBACK_H;
    }

    // The container is the hit-test target; a `pointertap` on the cup folds into the
    // double-click detector (Pixi v8 `pointertap` fires for any button, matching the
    // existing door/character tap pattern). `cursor: inherit` keeps the viewport's
    // gauntlet over the cup. The dice-wager context menu now opens on a **long-press**
    // of left-click (ADR-0169, replacing the shift+left-click of ADR-0161): a hold that
    // opened the menu latches {@link suppressClick}, so the trailing tap must be swallowed
    // rather than feeding the solo-roll double-click detector. The residual shift guard is
    // kept as a harmless defence so a stray shift+tap never primes it either.
    this.eventMode = 'static';
    this.cursor = 'inherit';
    this.on('pointertap', (e) => {
      if (e?.shiftKey) return;
      if (this.suppressClick?.()) return;
      this.registerTap();
    });
  }

  /**
   * Fold one tap into the double-click detector: a tap within {@link DOUBLE_CLICK_MS}
   * of the previous one fires {@link onDoubleClick} and consumes the pair; otherwise
   * it is recorded as the first tap of a potential pair. Uses the render clock so
   * the headless tests can drive it with a bare `emit`.
   */
  private registerTap(): void {
    const now = performance.now();
    if (now - this.lastTapMs <= DOUBLE_CLICK_MS) {
      this.lastTapMs = Number.NEGATIVE_INFINITY;
      this.onDoubleClick?.();
    } else {
      this.lastTapMs = now;
    }
  }

  /**
   * Float one emote line above the cup in `color` (a packed RGB). Plain text -
   * `BitmapText` never interprets markup - drawn in the baked UO font, stacked above
   * any still-visible earlier line, and faded/cleared on the render clock by
   * {@link update}. Used for the server's `*Name rolls d1,d2*` action emote.
   */
  say(text: string, color: number): void {
    const label = new BitmapText({
      text,
      style: {
        fontFamily: OVERHEAD_FONT_FAMILY,
        fontSize: OVERHEAD_FONT_SIZE,
        fill: color,
        align: 'center',
      },
    });
    label.anchor.set(0.5, 1);
    label.zIndex = LABEL_Z;
    this.addChild(label);
    // Draw in the always-on-top overhead pass so the emote never clips behind a wall
    // or roof, while staying this container's child for positioning + fade.
    this.overheadLayer?.attach(label);
    this.speechLines.push({ label, remainingMs: SPEECH_TTL_MS });
    this.layoutSpeech();
  }

  /** Set the persistent readable name shown over an interactive ground item. */
  setName(text: string | null, color = 0xe8d9a6): void {
    if (this.nameLabel?.text === text) return;
    if (this.nameLabel) {
      this.removeChild(this.nameLabel);
      this.nameLabel.destroy();
      this.nameLabel = undefined;
    }
    if (text) {
      const label = new BitmapText({
        text,
        style: {
          fontFamily: OVERHEAD_FONT_FAMILY,
          fontSize: OVERHEAD_FONT_SIZE,
          fill: color,
          align: 'center',
        },
      });
      label.anchor.set(0.5, 1);
      label.zIndex = LABEL_Z;
      this.addChild(label);
      this.overheadLayer?.attach(label);
      this.nameLabel = label;
    }
    this.layoutSpeech();
  }

  /**
   * Restack active lines bottom-up just above the cup's top edge: the newest line
   * takes the bottom, older ones climb above it. The base offset uses the billboard
   * height cached at construction (item 5 / ADR-0135), which adapts to the stub or
   * the baked art without re-measuring the fixed sprite on every relayout.
   */
  private layoutSpeech(): void {
    let y = -(this.billboardHeight + SPEECH_LINE_GAP);
    if (this.nameLabel) {
      this.nameLabel.position.set(0, y);
      y -= SPEECH_LINE_GAP;
    }
    for (let i = this.speechLines.length - 1; i >= 0; i--) {
      this.speechLines[i]!.label.position.set(0, y);
      y -= SPEECH_LINE_GAP;
    }
  }

  /** Age every active emote line, fade its tail, and drop the ones whose time is up. */
  update(dtMs: number): void {
    if (this.speechLines.length === 0) return;
    let removed = false;
    for (let i = this.speechLines.length - 1; i >= 0; i--) {
      const line = this.speechLines[i]!;
      line.remainingMs -= dtMs;
      if (line.remainingMs <= 0) {
        this.removeChild(line.label);
        line.label.destroy();
        this.speechLines.splice(i, 1);
        removed = true;
        continue;
      }
      line.label.alpha =
        line.remainingMs < SPEECH_FADE_MS ? line.remainingMs / SPEECH_FADE_MS : 1;
    }
    if (removed) this.layoutSpeech();
  }

  /** Tear down the cup and any active emote labels (the dice left the world). */
  dispose(): void {
    for (const line of this.speechLines) line.label.destroy();
    this.speechLines.length = 0;
    this.nameLabel = undefined;
    this.destroy({ children: true });
  }
}
